import React, { useState, useEffect } from 'react';
import { TrashIcon, PencilIcon, EyeIcon } from '@heroicons/react/24/outline';
import { getJobContacts } from '../../services/firebase';
import { createIOSSafariClickHandler } from '../../utils/iosSafariTouchFix';

export default function JobListTable({ jobs, isLoading, onJobSelect, onDeleteClick, onAddJobClick }) {
  const [jobContacts, setJobContacts] = useState({});

  useEffect(() => {
    if (jobs && jobs.length > 0) {
      fetchContactsForJobs(jobs);
    }
  }, [jobs]);

  const fetchContactsForJobs = async (jobsList) => {
    const contactsMap = {};

    for (const job of jobsList) {
      try {
        const result = await getJobContacts(job.id);
        if (result.success) {
          contactsMap[job.id] = result.contacts;
        }
      } catch (error) {
        console.error(`Error fetching contacts for job ${job.id}:`, error);
      }
    }

    setJobContacts(contactsMap);
  };



  if (isLoading) {
    return (
      <div className="px-4 py-5 sm:px-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          <div className="h-4 bg-gray-200 rounded w-5/6"></div>
        </div>
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <div className="px-4 py-5 sm:px-6 text-center">
        <p className="text-gray-500">No jobs found</p>
        <button
          type="button"
          onClick={onAddJobClick}
          className="mt-3 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Add Your First Job
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 table-container-scroll sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black/5 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                      Job Title
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Company
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Source
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Date Added
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Status
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Fastrak Contacts
                    </th>
                    <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {jobs.map((job) => {
                    const jobSelectHandlers = createIOSSafariClickHandler(() => onJobSelect(job.id));
                    return (
                    <tr key={job.id} className="hover:bg-gray-50 cursor-pointer" {...jobSelectHandlers}>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                        {job.title}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{job.company}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {job.source === 'admin' && (
                          <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20">
                            Admin
                          </span>
                        )}
                        {job.source === 'user' && (
                          <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                            User
                          </span>
                        )}
                        {job.source === 'employer' && (
                          <span className="inline-flex items-center rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 ring-1 ring-inset ring-purple-600/20">
                            Employer
                          </span>
                        )}
                        {!job.source && 'Unknown'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {job.createdAt ? new Date(job.createdAt.seconds * 1000).toLocaleDateString() : 'Unknown'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {job.status === 'active' && (
                          <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                            Active
                          </span>
                        )}
                        {job.status === 'inactive' && (
                          <span className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-600/20">
                            Inactive
                          </span>
                        )}
                        {job.status === 'pending' && (
                          <span className="inline-flex items-center rounded-md bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-700 ring-1 ring-inset ring-yellow-600/20">
                            Pending
                          </span>
                        )}
                        {!job.status && 'Unknown'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {jobContacts[job.id] && jobContacts[job.id].length > 0 ? (
                          <div>
                            <span className="text-gray-500">{jobContacts[job.id].length} contact{jobContacts[job.id].length !== 1 ? 's' : ''}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400">No contacts</span>
                        )}
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                        <div className="flex space-x-3 justify-end">
                          <button
                            type="button"
                            className="text-indigo-600 hover:text-indigo-900"
                            onClick={(e) => {
                              e.stopPropagation();
                              onJobSelect(job.id);
                            }}
                          >
                            <EyeIcon className="h-5 w-5" />
                            <span className="sr-only">View, {job.title}</span>
                          </button>
                          <button
                            type="button"
                            className="text-red-600 hover:text-red-900"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteClick(job);
                            }}
                          >
                            <TrashIcon className="h-5 w-5" />
                            <span className="sr-only">Delete, {job.title}</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}